import { useState, useRef, useEffect } from "react";
import { X, TriangleAlert, Check, CalendarDays, Pencil } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as Popover from "@radix-ui/react-popover";
import { springs } from "@/components/ui/motion";
import { UploadTagInput } from "./UploadTagInput";
import { CategoryPicker } from "../CategoryPicker";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileIcon } from "../FileIcon";
import { useTranslation } from "@/packages/utils";
import type { UploadFile } from "@/hooks/use-upload-documents";
import type { Id } from "@/packages/types";

/* ── file-type accent hex for progress ── */
const fileAccentHex: Record<string, string> = {
  pdf: "#dc2626",
  docx: "#2563eb",
  xlsx: "#059669",
  pptx: "#ea580c",
  txt: "#6b7280",
  image: "#9333ea",
};

interface UploadFileRowProps {
  file: UploadFile;
  onRename: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onAddTag: (fileId: string, tagId: string) => void;
  onRemoveTag: (fileId: string, tagId: string) => void;
  onSetCategoryId: (
    fileId: string,
    categoryId: Id<"documentCategories">,
  ) => void;
  onSetExpirationDate: (fileId: string, date: number | undefined) => void;
  disabled?: boolean;
  isDuplicate?: boolean;
  validationError?: string;
  shakeKey?: number;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

export function UploadFileRow({
  file,
  onRename,
  onRemove,
  onAddTag,
  onRemoveTag,
  onSetCategoryId,
  onSetExpirationDate,
  disabled,
  isDuplicate,
  validationError,
  shakeKey,
}: UploadFileRowProps) {
  const { t } = useTranslation(["documents", "common"]);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(file.displayName);
  const [shouldPulse, setShouldPulse] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (shakeKey && validationError) {
      setShouldPulse(true);
      const timer = setTimeout(() => setShouldPulse(false), 800);
      return () => clearTimeout(timer);
    }
  }, [shakeKey, validationError]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== file.displayName) {
      onRename(file.id, trimmed);
    } else {
      setEditValue(file.displayName);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setEditValue(file.displayName);
      setIsEditing(false);
    }
  };

  const isUploading = disabled;
  const isComplete = isUploading && file.progress >= 100;
  const showProgress = isUploading && file.progress > 0 && !isComplete;
  const hasCategoryError = validationError === "missing-category";
  const accentHex = fileAccentHex[file.fileType] ?? "#6b7280";

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4, scale: 0.98 }}
      transition={springs.smooth}
      className={`relative group rounded-lg border bg-card overflow-hidden transition-colors duration-200 ${
        validationError
          ? "border-red-500/30 bg-red-500/[0.02]"
          : isDuplicate
            ? "border-amber-500/30 bg-amber-500/[0.02]"
            : "border-border hover:border-border/80"
      }`}
    >
      {/* Error pulse overlay */}
      <AnimatePresence>
        {shouldPulse && validationError && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.06, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, repeat: 1, ease: "easeInOut" }}
            className="absolute inset-0 bg-red-500 pointer-events-none"
          />
        )}
      </AnimatePresence>

      {/* Progress background sweep */}
      {showProgress && (
        <div
          className="absolute inset-0 origin-left transition-transform duration-500 ease-out"
          style={{
            background: `linear-gradient(90deg, ${accentHex}06, ${accentHex}10)`,
            transform: `scaleX(${file.progress / 100})`,
          }}
        />
      )}

      <div className="relative px-3 py-3">
        {/* Row 1: Filename */}
        <div className="flex items-center gap-3 mb-2">
          {/* File type icon */}
          <FileIcon type={file.fileType} />

          {/* Filename */}
          {isEditing && !isUploading ? (
            <input
              ref={inputRef}
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              className="flex-1 min-w-0 bg-transparent text-[14px] font-medium text-primary outline-none border-b border-accent/40 pb-0.5"
            />
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!isUploading) {
                  setEditValue(file.displayName);
                  setIsEditing(true);
                }
              }}
              className="flex-1 min-w-0 text-[14px] font-medium text-primary truncate text-left cursor-text group/name inline-flex items-center gap-1.5"
            >
              <span className="truncate">{file.displayName}</span>
              {!isUploading && (
                <Pencil
                  size={11}
                  className="shrink-0 text-muted/0 group-hover/name:text-muted/50 transition-colors"
                />
              )}
            </button>
          )}

          {/* Duplicate badge */}
          {isDuplicate && !isUploading && (
            <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
              <TriangleAlert size={10} />
              {t("upload.duplicate")}
            </span>
          )}

          {/* Inline validation error text */}
          <AnimatePresence>
            {validationError && validationError !== "missing-category" && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-[11px] text-red-500 font-medium shrink-0"
              >
                {validationError}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Progress indicator */}
          {showProgress && (
            <div className="shrink-0 flex items-center gap-2">
              <div className="w-12 h-1 bg-border/30 rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: accentHex }}
                  animate={{ width: `${file.progress}%` }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              </div>
              <span className="text-[10px] font-medium text-muted tabular-nums">
                {Math.round(file.progress)}%
              </span>
            </div>
          )}

          {/* Completion checkmark */}
          {isComplete && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={springs.bouncy}
              className="shrink-0 flex items-center justify-center h-4.5 w-4.5 rounded-full bg-emerald-500"
            >
              <Check size={10} className="text-white" strokeWidth={3} />
            </motion.div>
          )}

          {/* Remove button */}
          {!isUploading && (
            <button
              type="button"
              onClick={() => onRemove(file.id)}
              className="p-1 rounded-md text-muted/60 hover:text-red-500 hover:bg-red-500/[0.06] transition-all cursor-pointer shrink-0"
              title={t("actions.removeFile")}
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Row 2: Inline metadata — category pill, tags, date */}
        {!isUploading && (
          <div className="flex items-center gap-2.5 ml-11 pt-2 mt-0 border-t border-border/60 flex-wrap">
            {/* Category picker (compact) */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: stops click from toggling card */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops propagation */}
            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
              <CategoryPicker
                selectedCategoryId={file.categoryId}
                onSelect={(categoryId) => onSetCategoryId(file.id, categoryId)}
              />
              {hasCategoryError && (
                <span className="text-[10px] text-red-500 font-medium mt-0.5 block">
                  {t("upload.selectCategory")}
                </span>
              )}
            </div>

            {/* Separator */}
            <span className="w-px h-3.5 bg-border shrink-0" />

            {/* Tags */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: stops click propagation */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops propagation */}
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex-1 min-w-0"
            >
              <UploadTagInput
                selectedTagIds={file.tags}
                onAdd={(tagId) => onAddTag(file.id, tagId)}
                onRemove={(tagId) => onRemoveTag(file.id, tagId)}
              />
            </div>

            {/* Expiration date */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: stops click propagation */}
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: only stops propagation */}
            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
              <DatePicker
                value={
                  file.expirationDate
                    ? new Date(file.expirationDate)
                    : undefined
                }
                onChange={(date) =>
                  onSetExpirationDate(file.id, date?.getTime())
                }
                minDate={new Date()}
              >
                <DatePicker.Trigger variant="input" className="text-[11px]">
                  {file.expirationDate ? (
                    <span className="inline-flex items-center gap-1 text-secondary/80">
                      <CalendarDays size={10} className="opacity-50" />
                      {new Date(file.expirationDate).toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-primary hover:text-secondary transition-colors">
                      <CalendarDays size={11} />
                      <span className="text-[11px]">Set expiry</span>
                    </span>
                  )}
                </DatePicker.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    side="bottom"
                    align="end"
                    sideOffset={4}
                    className="z-[60] rounded-[12px] border border-border bg-card shadow-lg"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                  >
                    <div className="flex">
                      <DatePicker.Presets />
                      <DatePicker.Calendar />
                    </div>
                    <DatePicker.Actions />
                  </Popover.Content>
                </Popover.Portal>
              </DatePicker>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
